import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, RotateCcw, Award } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { PageHeader, BackButton, AppButton, AppBadge, FilePreview, UserAvatar } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import "./general-assignments.scss";

const SubmissionReview = () => {
  const { submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [marks, setMarks] = useState("");
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    apiClient.get(`/general-assignments/submissions/${submissionId}`)
      .then(({ data }) => {
        setSubmission(data.submission);
        setAssignment(data.assignment);
        if (data.submission.marks != null) setMarks(String(data.submission.marks));
        if (data.submission.feedback) setFeedback(data.submission.feedback);
      })
      .catch(() => setToast({ show: true, message: "Failed to load submission", type: "error" }))
      .finally(() => setLoading(false));
  }, [submissionId]);

  const handleGrade = async () => {
    if (marks === "" || marks == null) {
      setToast({ show: true, message: "Please enter marks", type: "error" });
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.put(`/general-assignments/submissions/${submissionId}/grade`, {
        marks: Number(marks),
        feedback: feedback.trim(),
      });
      setSubmission(data.submission);
      setToast({ show: true, message: "Submission graded successfully", type: "success" });
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to grade", type: "error" });
    }
    setSaving(false);
  };

  const handleRequestResubmit = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put(`/general-assignments/submissions/${submissionId}/request-resubmit`, {
        feedback: feedback.trim(),
      });
      setSubmission(data.submission);
      setToast({ show: true, message: "Resubmission requested", type: "success" });
    } catch {
      setToast({ show: true, message: "Failed to request resubmission", type: "error" });
    }
    setSaving(false);
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  if (loading) return <div className="ga-page ga-loading">Loading...</div>;
  if (!submission) return <div className="ga-page"><BackButton />Not found</div>;

  const student = submission.studentId || {};

  return (
    <div className="ga-page">
      <PageHeader
        backButton
        icon={Award}
        title="Review Submission"
        description={assignment?.title || ""}
      />

      <div className="ga-review">
        {/* Student info */}
        <div className="ga-review__section">
          <div className="ga-review__section-label">Student</div>
          <div className="ga-detail__student-info">
            <UserAvatar name={student.name} size={32} />
            <div>
              <strong>{student.name || "Unknown"}</strong>
              {student.email && <div style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>{student.email}</div>}
            </div>
          </div>
          <div className="ga-detail__meta" style={{ marginTop: "0.5rem" }}>
            <span className="ga-detail__meta-item">Submitted: {formatDate(submission.submittedAt)}</span>
            <AppBadge
              variant="outline"
              color={
                submission.status === "graded" ? "emerald" :
                submission.status === "resubmit_requested" ? "amber" : "blue"
              }
            >
              {submission.status === "resubmit_requested" ? "Resubmit Requested" : submission.status}
            </AppBadge>
          </div>
        </div>

        {/* Text response */}
        {submission.textResponse && (
          <div className="ga-review__section">
            <div className="ga-review__section-label">Response</div>
            <div className="ga-review__text-response">{submission.textResponse}</div>
          </div>
        )}

        {/* Attachments */}
        {submission.attachments?.length > 0 && (
          <div className="ga-review__section">
            <div className="ga-review__section-label">Attachments</div>
            <FilePreview attachments={submission.attachments} />
          </div>
        )}

        {/* Grading form */}
        <div className="ga-review__section">
          <div className="ga-review__section-label">Grading</div>
          <div className="ga-review__grade-form">
            <div className="ga-review__marks-row">
              <input
                type="number"
                min="0"
                max={assignment?.maxMarks || 100}
                className="ga-review__marks-input"
                value={marks}
                onChange={(e) => setMarks(e.target.value)}
                placeholder="0"
              />
              <span className="ga-review__max-marks">/ {assignment?.maxMarks || 100}</span>
            </div>

            <div className="ga-form__group" style={{ marginBottom: 0 }}>
              <label className="ga-form__label">Feedback</label>
              <textarea
                className="ga-form__textarea"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Add feedback for the student..."
                rows={3}
              />
            </div>

            <div className="ga-form__actions" style={{ marginTop: "0.5rem" }}>
              <AppButton onClick={handleGrade} disabled={saving}>
                <CheckCircle size={16} />
                {submission.status === "graded" ? "Update Grade" : "Grade & Approve"}
              </AppButton>
              {submission.status !== "resubmit_requested" && (
                <AppButton variant="outline" onClick={handleRequestResubmit} disabled={saving}>
                  <RotateCcw size={16} /> Request Resubmit
                </AppButton>
              )}
            </div>

            {submission.status === "graded" && (
              <div className="ga-review__status-info ga-review__status-info--graded">
                <CheckCircle size={16} />
                Graded: {submission.marks}/{assignment?.maxMarks} — by {submission.gradedBy?.name || "Unknown"} on {formatDate(submission.gradedAt)}
              </div>
            )}
          </div>
        </div>
      </div>

      <CommonToaster toast={toast} setToast={setToast} />
    </div>
  );
};

export default SubmissionReview;
